import { HttpStatusCode } from "axios";
import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { CATEGORY, CATEGORY_ALIAS } from "../../../types/talk";

import { findCategoryForContext } from "../../../libs/category";
import { safeReplaceAll } from "../../../libs/replace";

import { useSandiTalk } from "../../../contexts/boardContext";
import { useUserContext } from "../../../contexts/userContext";

import {
  getTalkCategories,
  TalkCategory,
} from "../../../services/talkServices";

import useSimplePopup from "../../../hooks/useSimplePopup";

type Props = {};

// eslint-disable-next-line no-empty-pattern
function TalkOutlet(props: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const [talk, { loadCategory, updateCategory, updateField }] = useSandiTalk();
  const [user] = useUserContext();
  const [simplePopupComponent, { openError }] = useSimplePopup({});
  const [lifeCategoryIdState, setLifeCategoryIdState] = useState<number | undefined>(location.state?.lifeCategoryId ?? undefined);

  const addAllCategoryForMain = (
    category: TalkCategory,
    baseId?: number
  ): TalkCategory => {
    const allCategory = [
      {
        lifeCategoryId: (baseId ?? 0) + CATEGORY.ALL,
        alias: category.alias + "-all",
        name: "전체",
        url: category?.url ?? "",
      },
      {
        lifeCategoryId: (baseId ?? 0) + CATEGORY.POPULAR,
        alias: category.alias + "-popular",
        name: "인기글",
        url: safeReplaceAll(category?.url ?? "", "GROUP", "ALL_DEEP") ?? "",
      },
    ] as TalkCategory[];

    return {
      ...category,
      lifeCategoryList: [
        ...(category.alias === CATEGORY_ALIAS.TALK
          ? allCategory
          : [allCategory[0]]),
        ...(category?.lifeCategoryList?.map((category) =>
          addAllCategoryForSub(category)
        ) ?? []),
      ],
    };
  };

  const addAllCategoryForSub = (category: TalkCategory): TalkCategory => {
    const allCategory = {
      lifeCategoryId: CATEGORY.ALL,
      alias: category.alias + "-all",
      name: "전체",
      url: category?.url ?? "",
    } as TalkCategory;

    return {
      ...category,
      lifeCategoryList: [allCategory, ...(category?.lifeCategoryList ?? [])],
    };
  };

  const getCategory = async () => {
    if (!talk?.buildingId) return;
    
    let targetCategory: number;
    if (lifeCategoryIdState) {
      targetCategory = lifeCategoryIdState;
    } else {
      targetCategory = CATEGORY.ALL; // 샌디톡 Tab 기본 선택 설정
    }

    await updateField({ isLoading: true });
    try {
      const result = await getTalkCategories({
        buildingId: talk?.buildingId,
      });
      if (result.status === HttpStatusCode.Ok) {
        // [SANDI-1432] 샌디톡 Tab에서 '건물톡/샌디톡' 순서로 정렬하기 위해 sort 처리
        const mainCategory = result.data;

        if (!result.data.find((item) => item.name === "건물")) {
          mainCategory.push({
            lifeCategoryId: CATEGORY.BUILDING,
            alias: CATEGORY_ALIAS.BUILDING,
            name: "건물",
            url: "",
            lifeCategoryList: [],
          });
        }
        const mainCategoryWithAll = result.data.map((category, index) =>
          addAllCategoryForMain(category, index * -10)
        );

        await loadCategory({ categoryList: mainCategoryWithAll });
        await updateCategory({
          categoryInfo:
            findCategoryForContext(
              mainCategoryWithAll,
              "lifeCategoryId",
              targetCategory,
              0
            ) ?? {},
        });
      } else {
        throw new Error("카테고리 조회 실패");
      }
    } catch (e: any) {
      openError(e, "카테고리 조회 실패", {
        onClick: () => {
          navigate("/view");
        },
      });
    } finally {
      await updateField({ isLoading: false });
      setLifeCategoryIdState(undefined);
    }
  };

  useEffect(() => {
    if (user.isLoading) return;
    getCategory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [talk.buildingId, user.isLoading]);

  useEffect(() => {
    if (user.isLoading) return;
    if (user.buildingId) {
      updateField({
        buildingId: user.buildingId,
        buildingName: user.buildingName,
      });
    } else {
      getCategory();
    }
  }, [user.isLoading, user.buildingId, user.buildingName]);

  return (
    <div data-testid="talk-outlet">
      <Outlet />
      {simplePopupComponent}
    </div>
  );
}

export default TalkOutlet;
